import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { editSVG, trashSVG } from '../../lib/allCustomSVGs';
import '../../styles/pages/MCP/mcpConfigBox.scss';
import '../../styles/pages/MCP/requestMCP.scss';
import '../../styles/pages/skills/createSkillModal.scss';
import '../../styles/pages/prompt-schedules/viewPromptScheduleModal.scss';
import type { PromptSchedule } from '../../lib/api/prompt-schedules/types';
import makeFirstLetterCapital from '../../lib/helper-functions/makeFirstLetterCapital';

function formatDays(days: (string | number)[] | undefined): string {
  if (!days?.length) return '—';
  return days.map(String).join(', ');
}

interface ViewPromptScheduleModalProps {
  schedule: PromptSchedule;
  onEdit: () => void;
  onDelete: () => void;
  deleteDisabled?: boolean;
}

const ViewPromptScheduleModal = ({
  schedule,
  onEdit,
  onDelete,
  deleteDisabled = false,
}: ViewPromptScheduleModalProps) => {
  const { t } = useTranslation();

  return (
    <div className='mcpConfigBox createSkillModal viewSkillModal'>
      <div className='requestMCP__titles'>
        <h3>{t('ViewPromptSchedule')}</h3>
        <span>{t('ViewPromptScheduleSubtitle')}</span>
      </div>

      <div className='mcpConfigBox__content'>
        <div className='mcpConfigBox__content--inputs'>
          <div className='viewPromptScheduleModal__row'>
            <strong>{t('PromptScheduleName')}:</strong>{' '}
            {schedule.name?.trim() ? schedule.name : '—'}
          </div>
          <div className='viewPromptScheduleModal__row'>
            <strong>{t('PromptScheduleType')}:</strong>{' '}
            {makeFirstLetterCapital(schedule.schedule_type)}
          </div>
          <div className='viewPromptScheduleModal__row'>
            <strong>{t('PromptScheduleTime')}:</strong> {schedule.schedule_time}{' '}
            · {schedule.timezone}
          </div>
          {schedule.schedule_type !== 'daily' && (
            <div className='viewPromptScheduleModal__row'>
              <strong>{t('PromptScheduleDaysLabel')}:</strong>{' '}
              {formatDays(schedule.schedule_days)}
            </div>
          )}
          {schedule.next_run_at && (
            <div className='viewPromptScheduleModal__row'>
              <strong>{t('PromptScheduleNextRun')}:</strong>{' '}
              {new Date(schedule.next_run_at).toLocaleString()}
            </div>
          )}
          <div className='viewPromptScheduleModal__promptWrap'>
            <div
              className='viewPromptScheduleModal__row viewPromptScheduleModal__row--promptLabel'
              id='view-ps-prompt-heading'
            >
              <strong>{t('PromptSchedulePromptLabel')}:</strong>
            </div>
            <textarea
              id='view-ps-prompt'
              className='viewPromptScheduleModal__promptReadonlyInput'
              value={schedule.prompt ?? ''}
              readOnly
              rows={6}
              tabIndex={-1}
              aria-labelledby='view-ps-prompt-heading'
            />
          </div>
        </div>
      </div>

      <div className='mcpConfigBox__buttons viewPromptScheduleModal__footer'>
        <MainSquareButton
          type='button'
          value={deleteDisabled ? `${t('Deleting')}...` : t('Delete')}
          onClick={onDelete}
          classes='mcpConfigBox__buttons--danger'
          icon={trashSVG}
          disabled={deleteDisabled}
        />
        <MainSquareButton
          type='button'
          value={t('Edit')}
          onClick={onEdit}
          classes='mcpConfigBox__buttons--update'
          icon={editSVG}
          disabled={deleteDisabled}
        />
      </div>
    </div>
  );
};

export default ViewPromptScheduleModal;
